import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSavePassword } from '@sentara-health/sentara-api-hooks-core';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ChangePassword,
  ConfirmPassword,
  PasswordStrengthMeter,
  PasswordShowEmpty,
} from 'templates';
import {
  CheckIconSmall,
  useAuth,
  setStrengthBarColor,
  deleteEncryptedValue,
  errorhandler,
  userLockedOut,
  domainForSegment,
  ANALYTICS_MESSAGES,
} from 'common';

// Import required components from SentaraUI library
import {
  Button,
  ModalOverlay,
  Paragraph,
  ModalOverlayWithoutClose,
} from '@sentara-health/optimahealth_web';
import { deleteCacheStorage } from '@sentara-health/cachestorage';
import { GlobalContext } from 'context';
import { PASSWORD_CRITERIA } from '../../pages/forgot-password/PasswordConstant';
import { PasswordChangeProps, FormInputs } from './interface';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
// Define the interface for the props passed to PasswordChange component

// Define the PasswordChange component
const PasswordChange = ({
  changePassword,
  setChangePassword,
  state,
}: PasswordChangeProps) => {
  // Initialize localization using react-i18next
  const { t } = useTranslation();
  const methods = useForm<FormInputs>({});
  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;
  const auth: any = useAuth();
  // State variables for various functionalities
  const { getFeature } = useContext(GlobalContext);
  const { savePassword } = useSavePassword();
  const [isDisabled, setIsDisabled] = useState(true);
  const [loader, setLoader] = useState(false);
  const [pwdStrength, setPwdStrength] = useState(0);
  const [value, setValue] = useState<any>('');
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const { contentListingItemClicked } = useAdobeAnalytics();

  const trackContentListing = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}/${window.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [{ contentId, contentTitle, itemPosition }],
        },
      },
    });
  };

  useEffect(() => {
    setStrengthBarColor(value, setPwdStrength);
  }, [value]);

  // Handle the "Change Password" button click
  const handleChangeButton = () => {
    trackContentListing('622', ANALYTICS_MESSAGES.PASSWORD_EDIT_SELECTED, 25);
    setChangePassword(true);
    setIsDisabled(false);
  };

  // Handle user sign out
  const signOutHandler = async () => {
    localStorage.clear();
    deleteEncryptedValue();
    auth.logout(true);
    await deleteCacheStorage();
    window.location.href = '/sign-in';
  };

  // State for the modal
  const [isOpen, setIsOpen] = useState(false);

  // state for Account Lockout modal
  const [isAccountLockoutModalOpen, setIsAccountLockoutModalOpen] =
    useState(false);

  // Function to close the modal
  function closeModal() {
    setIsOpen(false);
    signOutHandler();
  }

  const callSavePassword = async (password: string) => {
    const response = await savePassword(
      '',
      getFeature?.AccountSettings,
      password
    );

    const errorCode = response?.errorCode;
    if (errorCode) {
      // Account lockout popup
      setPasswordError(true);
      if (response?.errorDetails === userLockedOut) {
        setIsAccountLockoutModalOpen(true);
        trackContentListing(ANALYTICS_MESSAGES.POP_ACCOUNT_LOCKOUT, 'Lockout', 1);
      } else {
        errorhandler(errorCode);
      }
    } else {
      setIsOpen(true);
      setPasswordError(false);
      trackContentListing('', ANALYTICS_MESSAGES.PASSWORD_SAVE, 26);
    }
  };
  // Handle form submission
  const formSubmitHandler = async (data: FormInputs) => {
    if (pwdStrength !== 4 || passwordError) {
      return;
    }
    setLoader(true);
    await callSavePassword(data?.password);
    setLoader(false);
  };

  // handle cancel button
  const handleCancel = () => {
    trackContentListing('', ANALYTICS_MESSAGES.PASSWORD_EDIT_CANCEL, 28);
    setChangePassword(!changePassword);
    reset();
    setPwdStrength(0);
    setPasswordError(false);
  };

  // Handle key down event
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Space') {
      e.preventDefault();
    }
  };

  // Determine the class name for password strength
  const inputStrengthClass =
    pwdStrength in PASSWORD_CRITERIA
      ? `strength-bar-${PASSWORD_CRITERIA[pwdStrength]}`
      : '';

  return (
    <>
      {changePassword ? (
        // Render form for changing password

        <FormProvider {...methods}>
          <form
            data-testid="passwordInputLabelContainer"
            onSubmit={handleSubmit(formSubmitHandler)}
          >
            <div className="col-xl-6 col-md-6 col-12">
              <div className="col-xl-10 col-lg-10 col-md-10 col-sm-12 col-12">
                <Paragraph
                  text={t('profile.passwordInformation')}
                  variant="regular"
                  data-testid="profile.passwordInformation"
                  className="pb-3 mb-2"
                />
                {/* Render ChangePassword component */}
                <ChangePassword
                  inputStrengthClass={inputStrengthClass}
                  onKeyDown={onKeyDown}
                  setValue={setValue}
                  state={state}
                  setPasswordError={setPasswordError}
                  errorPassword={passwordError}
                  showPasswordPlaceholder={`${t('common.passwordPlaceholder')}`}
                />
              </div>
              <div className="pt-3 pb-3 col-xl-10 col-lg-10 col-md-10 col-sm-12 col-12">
                {/* Render PasswordStrengthMeter component */}
                <PasswordStrengthMeter
                  inputStrengthClass={inputStrengthClass}
                />
              </div>
              <div className="col-xl-10 col-lg-10 col-md-10 col-sm-12 col-12 w-100 float-left d-flex">
                {/* Render ConfirmPassword component */}
                <div className="col-xl-10 col-lg-10 col-md-10 col-sm-12 col-12">
                  <ConfirmPassword
                    onKeyDown={onKeyDown}
                    showPasswordPlaceholder={`${t('common.confirmPassword')}`}
                  />
                </div>
              </div>
            </div>

            <hr />
            <div className="row d-flex justify-content-start align-items-center py-2">
              <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                <Button
                  dataTestId="submitButton"
                  variant="primary_btn"
                  disabled={!isValid || passwordError}
                  type="submit"
                >
                  {t('common.save')}
                </Button>
              </div>
              <div className="col-sm-auto col-12 mb-sm-0">
                <Button
                  dataTestId="cancelButton"
                  variant="secondary_btn"
                  onClick={handleCancel}
                >
                  {t('common.cancel')}
                </Button>
              </div>
              {loader && <div className="loader"></div>}
            </div>
            {/* Render modal overlay when isOpen is true */}
            {isOpen && (
              <ModalOverlay
                isOpen
                onClose={closeModal}
                overlayInfo="Modal information"
              >
                <div data-testid="memberIdModal">
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <CheckIconSmall />
                  </div>
                  <h5
                    data-testid="loggedMessage"
                    className="label_logged_message"
                  >
                    {t('profile.logged')}{' '}
                  </h5>
                </div>
              </ModalOverlay>
            )}

            {/* Render account lockout modal overlay when isAccountLockoutModalOpen is true */}
            {isAccountLockoutModalOpen && (
              <ModalOverlayWithoutClose
                isOpen
                onClose={() => {}}
                overlayInfo={t('common.maximumAttemptInfo')}
              >
                <div data-testid="maximumAttemptInfo">
                  {t('common.accountLockoutMessage')}
                  <div className="d-flex align-items-center justify-content-center mt-3">
                    <Button
                      dataTestId="okButton"
                      variant="primary_btn"
                      onClick={closeModal}
                    >
                      {t('common.ok')}
                    </Button>
                  </div>
                </div>
              </ModalOverlayWithoutClose>
            )}
          </form>
        </FormProvider>
      ) : (
        // Render PasswordShowEmpty component if changePassword is false
        <PasswordShowEmpty
          isDisabled={isDisabled}
          handleChangeButton={handleChangeButton}
        />
      )}
    </>
  );
};

export default PasswordChange;
